// @flow
import type { Node } from 'react';
import React, { Component } from 'react';
import { Trans } from '@lingui/react';
import classNames from 'classnames';
import styled, { type StyledComponent } from 'styled-components';

import { DSTokenMap } from '@rover/kibble/styles';
import { FORM_ERROR_CLASSNAME } from '@rover/react-lib/constants';
import type { Untranslated } from '@rover/shared/js/types';

import Popover from '../../callouts/Popover';

export type Props = {
  placement: Popover.Placement,
  children: Node,
  message: ?Untranslated,
  className?: string,
  didSubmit?: boolean,
  canDismiss?: boolean,
};

type State = {
  lastMessage: Untranslated,
  isDismissed: boolean,
};

export const ErrorWrapper: StyledComponent<{}> = styled.div`
  color: ${DSTokenMap.TEXT_COLOR_ERROR};
`;

const getClassNames = (message, className) =>
  classNames(className, {
    [FORM_ERROR_CLASSNAME]: message,
  });

export default class FormValidationError extends Component<Props, State> {
  popover: { current: null | Popover };

  static defaultProps = {
    placement: Popover.Placement.TOP,
    message: '',
  };

  constructor(props: Props) {
    super(props);
    this.popover = React.createRef();
    if (props.canDismiss) {
      FormValidationError.canDismiss = true;
    }
  }

  state = {
    lastMessage: '',
    isDismissed: false,
  };

  componentDidMount() {
    if (process.env.JS_ENV_CLIENT) {
      document.addEventListener(
        'click',
        function click() {
          return () => this.handleClick();
        },
        false
      );
    }
  }

  componentWillReceiveProps(newProps: Props) {
    // Make sure all FormValdiationErrors get that there was just a submit
    if (newProps.didSubmit) {
      FormValidationError.didSubmit = true;
    }

    const isNewSubmission = FormValidationError.didSubmit;
    const isNewError = this.props.message !== newProps.message;

    // All new submissions and new errors should reset the dismissal
    if (isNewSubmission || isNewError) {
      this.setState(() => ({
        lastMessage: newProps.message || '',
        isDismissed: false,
      }));
    }
  }

  componentWillUnmount() {
    if (process.env.JS_ENV_CLIENT) {
      document.removeEventListener(
        'click',
        function click() {
          return () => this.handleClick();
        },
        false
      );
    }
  }

  // If the last action was a submit, validation messages will reappear.
  static didSubmit = true;

  // Determines if the validaiton errors can be dismissed.
  // canDismiss will be passed in as a prop if it validation messages can be dismissed.
  static canDismiss = false;

  handleClick = () => {
    // Dismiss validation messages when clicked.
    if (FormValidationError.canDismiss) {
      this.setState(() => ({
        isDismissed: true,
      }));
      FormValidationError.didSubmit = false;
    }
  };

  renderContent() {
    const { message } = this.props;
    const { lastMessage } = this.state;

    return (
      <ErrorWrapper>
        <Trans id={message || lastMessage} />
      </ErrorWrapper>
    );
  }

  render() {
    const { placement, children, message, className, ...other } = this.props;
    const { isDismissed } = this.state;
    return (
      <Popover
        ref={this.popover}
        onClick={() => {
          this.handleClick();
        }}
        event={Popover.Event.NONE}
        placement={placement}
        content={this.renderContent()}
        open={!!message && !isDismissed}
        display="block"
        className={getClassNames(message, className)}
        {...other}
      >
        {children}
      </Popover>
    );
  }
}



// WEBPACK FOOTER //
// ../react-lib/src/components/formFields/FormValidationError/FormValidationError.jsx